"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Star, Quote } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const testimonials = [
  {
    name: "Carlos Mendoza",
    position: "Freelancer, Diseñador Gráfico",
    avatar: "CM",
    quote:
      "Finanza.lat cambió mi forma de administrar mis finanzas. Ahora tengo claridad total sobre mis ingresos y gastos, lo que me permite tomar mejores decisiones para mi negocio freelance.",
    rating: 5,
    userType: "freelancer",
  },
  {
    name: "María Rodríguez",
    position: "Fundadora, Startup de Tecnología",
    avatar: "MR",
    quote:
      "La capacidad de manejar múltiples monedas ha sido esencial para mi empresa que trabaja con clientes internacionales. Los reportes automáticos me ahorran horas de trabajo cada mes.",
    rating: 5,
    userType: "business",
  },
  {
    name: "Jorge Vargas",
    position: "Propietario, Restaurante Local",
    avatar: "JV",
    quote:
      "Como pequeño empresario, necesitaba una solución que no fuera complicada pero completa. Finanza.lat es perfecta: fácil de usar pero con todas las funciones que necesito.",
    rating: 4,
    userType: "mype",
  },
  {
    name: "Lucía Torres",
    position: "Consultora Independiente",
    avatar: "LT",
    quote:
      "La integración con el sistema tributario peruano es impresionante. Simplifica enormemente mis declaraciones de impuestos y me mantiene al día con mis obligaciones fiscales.",
    rating: 5,
    userType: "freelancer",
  },
  {
    name: "Miguel Sánchez",
    position: "CEO, Agencia de Marketing",
    avatar: "MS",
    quote:
      "Gestionar las finanzas de un equipo de 15 personas solía ser un dolor de cabeza constante. Con Finanza.lat, ahora es un proceso fluido y transparente.",
    rating: 5,
    userType: "business",
  },
  {
    name: "Daniela López",
    position: "Dueña, Tienda Online",
    avatar: "DL",
    quote:
      "Los recordatorios de pagos y la conciliación automática han eliminado por completo los pagos tardíos en mi negocio. El soporte al cliente es excepcional.",
    rating: 4,
    userType: "mype",
  },
];

export default function Testimonials() {
  const [mounted, setMounted] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const filteredTestimonials =
    filter === "all"
      ? testimonials
      : testimonials.filter((t) => t.userType === filter);

  return (
    <section id="testimonios" className="py-20 relative bg-muted/30">
      {/* Background gradient effect */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-chart-5/10 rounded-full blur-3xl dark:bg-chart-5/5" />

      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Lo que dicen
            <span className="bg-gradient-to-r from-chart-4 to-chart-5 text-transparent bg-clip-text block mt-1">
              nuestros usuarios
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Descubre cómo finanza.lat ha transformado la gestión financiera de
            freelancers, empresas y MYPEs en Perú.
          </p>
        </motion.div>

        <div className="flex justify-center mb-10">
          <div className="inline-flex p-1 bg-muted rounded-lg">
            <button
              onClick={() => setFilter("all")}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-all",
                filter === "all"
                  ? "bg-card shadow-sm"
                  : "hover:bg-muted-foreground/10"
              )}
            >
              Todos
            </button>
            <button
              onClick={() => setFilter("freelancer")}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-all",
                filter === "freelancer"
                  ? "bg-card shadow-sm"
                  : "hover:bg-muted-foreground/10"
              )}
            >
              Freelancers
            </button>
            <button
              onClick={() => setFilter("business")}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-all",
                filter === "business"
                  ? "bg-card shadow-sm"
                  : "hover:bg-muted-foreground/10"
              )}
            >
              Empresas
            </button>
            <button
              onClick={() => setFilter("mype")}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-all",
                filter === "mype"
                  ? "bg-card shadow-sm"
                  : "hover:bg-muted-foreground/10"
              )}
            >
              MYPEs
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTestimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full border-muted/40 bg-card/50 backdrop-blur">
                <CardContent className="p-6">
                  <Quote className="h-8 w-8 text-chart-4/40 mb-4" />

                  <div className="flex mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          "h-4 w-4",
                          i < testimonial.rating
                            ? "text-chart-4 fill-chart-4"
                            : "text-muted-foreground/30"
                        )}
                      />
                    ))}
                  </div>

                  <p className="text-muted-foreground mb-6">
                    "{testimonial.quote}"
                  </p>

                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback className="bg-chart-1/20 text-chart-1">
                        {testimonial.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.position}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
